import React, { useEffect, useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import { addDoc, collection, doc, getDoc, Timestamp } from "firebase/firestore";
import Swal from "sweetalert2";

import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    CircularProgress,
    Grid
} from "@mui/material";

import ConstructionIcon from '@mui/icons-material/Construction';

function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [systemDetails, setSystemDetails] = useState({});

    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000
    });

    // System details
    useEffect(() => {
        setSystemDetails({
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });
    }, []);

    // Auto login if stored
    useEffect(() => {
        const savedEmail = localStorage.getItem("email");
        const savedPassword = localStorage.getItem("password");
        if (savedEmail && savedPassword) {
            setEmail(savedEmail);
            setPassword(savedPassword);
            login(savedEmail, savedPassword, true);
        }
    }, []);

    const login = async (emailParam, passwordParam, auto = false) => {
        setLoading(true);
        try {
            const userEmail = auto ? emailParam : email;
            const userPassword = auto ? passwordParam : password;

            await signInWithEmailAndPassword(auth, userEmail, userPassword);
            const user = auth.currentUser;

            if (user) {
                const docRef = doc(db, "users", user.uid);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists() && docSnap.data().role === "Manager") {
                    if (!auto) {
                        localStorage.setItem("email", email);
                        localStorage.setItem("password", password);
                    }

                    await addDoc(collection(db, "logindetails"), {
                        uid: user.uid,
                        username: docSnap.data().name,
                        systemDetails,
                        createdTime: Timestamp.now()
                    });

                    Toast.fire({
                        icon: "success",
                        title: "Login Successful"
                    });

                    window.location.href = "/home";
                } else {
                    Toast.fire({
                        icon: "error",
                        title: "Access Denied"
                    });
                    auth.signOut();
                    localStorage.removeItem("email");
                    localStorage.removeItem("password");
                }
            }
        } catch (error) {
            console.log(error.message);
            
            if (!auto) {
                Toast.fire({
                    icon: "error",
                    title: "Invalid Credentials"
                });
            }
            localStorage.removeItem("email");
            localStorage.removeItem("password");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ height: "100vh", display: "flex" }}>
            {/* LEFT SIDE LOGO/BRAND */}
            <Box
                sx={{
                    flex: 1,
                    backgroundColor: "#fff", // white background
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    justifyContent: "center",
                    p: 8,
                    position: "relative",
                    overflow: "hidden"
                }}
            >
                <Box sx={{
                    position: "relative", zIndex: 2, maxWidth: 500, textAlign: "center"
                }}>
                    <img src="/logo.jpg" height={100} alt="Company Logo" />
                    <Typography sx={{
                        fontSize: 38,
                        fontWeight: 800,
                        mt: 2,
                        lineHeight: 1.2
                    }}>
                        Tejas Stone Crusher
                    </Typography>
                    <Typography sx={{ mt: 3, fontSize: 18, opacity: 0.9 }}>
                        Stone Crusher Management Portal
                    </Typography>
                </Box>
            </Box>

            {/* LOGIN SECTION */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg,blue,#ffe0b2)" // light orange background
                }}
            >
                <Paper sx={{
                    width: 380,
                    p: 5,
                    borderRadius: 4,
                    boxShadow: "0 20px 60px rgba(0,0,0,0.15)"
                }}>
                    <Typography variant="h5" fontWeight="bold" textAlign="center" mb={1}>
                        Login Portal
                    </Typography>
                    <Typography textAlign="center" sx={{ mb: 4, color: "#777" }}>
                        Enter your credentials to continue
                    </Typography>
                    <TextField
                        fullWidth
                        label="Email Address"
                        size="small"
                        sx={{ mb: 2 }}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <TextField
                        fullWidth
                        label="Password"
                        type="password"
                        size="small"
                        sx={{ mb: 3 }}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => login()}
                        disabled={loading}
                        sx={{
                            py: 1.4,
                            fontWeight: "bold",
                            borderRadius: 2,
                            background: "linear-gradient(90deg,blue,#ffb347)"
                        }}
                    >
                        {loading
                            ? <CircularProgress size={24} color="inherit" />
                            : "Login"}
                    </Button>
                    <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 4, color: "#777" }}>
                        © {new Date().getFullYear()} Tejas Stone Crusher
                    </Typography>
                </Paper>
            </Box>
        </Box>
    );
}

export default Login;